import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import {
	Button as BuiButton,
	DatePicker,
	type DatePickerProps,
	Dialog as BuiDialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	Flex,
	Text,
	TextAreaField,
} from "@backstage/ui";
import {
	AVD_PLUGIN_ID,
	BUNDLE_HOLD_LIMITS,
	type BundleHold,
	type BundleHoldKind,
	type BundleHoldRequest,
	type BundleHoldResponse,
	type MyBundleHoldResponse,
	bundleHoldPath,
	bundleHoldRequestErrors,
	bundleMyHoldPath,
	holdMaxUntil,
	isHoldActive,
} from "@internal/backstage-plugin-avd-common";
import { parseDate } from "@internationalized/date";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { deleteOnText } from "./myDesktop";

// Holds on both surfaces: the owner's self-service hold (My AVD) and an admin's hold on anyone's bundle
// (the admin page). Rules and limits: docs/infra/bundle-contract.md § Holds; the server enforces them.

const useStyles = makeStyles((theme) => ({
	row: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: theme.spacing(1),
	},
	actions: {
		display: "flex",
		gap: theme.spacing(1),
	},
	danger: { color: "var(--bui-fg-danger)" },
}));

// BUI nests its own copy of @internationalized/date, so its DateValue is a different class to ours.
const asDateValue = <K extends "defaultValue" | "minValue" | "maxValue">(day: string) =>
	parseDate(day) as unknown as DatePickerProps[K];

/** The `YYYY-MM-DD` (UTC) of an instant: what a date input holds. */
export function utcDay(at: Date): string {
	return at.toISOString().slice(0, 10);
}

/** The hold end for a picked day: the end of that UTC day, capped at `max` so the last day stays pickable. */
export function holdUntilFor(day: string, max: Date): string {
	return new Date(Math.min(Date.parse(`${day}T23:59:59.000Z`), max.getTime())).toISOString();
}

/** A hold in words, for the owner: whose it is matters, because only an admin can change an admin's. */
export function myHoldText(hold: BundleHold): string {
	const by = hold.kind === "admin" ? " by the platform team" : "";
	return `Kept until ${deleteOnText(hold.holdUntil)}${by}${hold.reason ? `: ${hold.reason}` : ""}.`;
}

/** Why the owner cannot place a hold now, in words. */
export function selfHoldRefusalText(refused: MyBundleHoldResponse["refused"]): string | undefined {
	switch (refused) {
		case "admin-hold":
			return "The platform team placed this hold, so only they can change it.";
		case "renewals-used":
			return `You have used all ${BUNDLE_HOLD_LIMITS.selfMaxRenewals + 1} self-service holds. Ask the platform team for a longer one.`;
		default:
			return undefined;
	}
}

type HoldResult<T> = { value: T; error?: undefined } | { value?: undefined; error: string };

/** The server's own refusal (a Backstage error body) where it gave one. */
async function refusal(res: Response): Promise<string> {
	try {
		const body = (await res.json()) as { error?: { message?: unknown } };
		if (typeof body.error?.message === "string") return body.error.message;
	} catch {
		// Not JSON: fall through to the status.
	}
	return `The hold could not be changed (HTTP ${res.status}).`;
}

/** One call to a hold route, resolved to the parsed body or a message to show. Never throws. */
function useHoldRequest() {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	return useCallback(
		async <T,>(
			method: "GET" | "PUT" | "DELETE",
			path: string,
			body?: BundleHoldRequest,
		): Promise<HoldResult<T>> => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(`${baseUrl}${path}`, {
					method,
					headers: body ? { "Content-Type": "application/json" } : undefined,
					body: body ? JSON.stringify(body) : undefined,
				});
				if (!res.ok) return { error: await refusal(res) };
				return { value: (await res.json()) as T };
			} catch {
				return { error: "The platform could not be reached. Try again." };
			}
		},
		[discoveryApi, fetchApi],
	);
}

export type HoldDialogProps = {
	open: boolean;
	title: string;
	kind: BundleHoldKind;
	/** ISO-8601. The latest end the server will accept for this caller now. */
	max: string;
	initialReason?: string;
	onCancel: () => void;
	/** Resolves to the message to show when the server refused, or undefined on success. */
	onSubmit: (request: BundleHoldRequest) => Promise<string | undefined>;
};

/** Picks the end day and the reason. Checks the rules before sending; the server's answer still wins. */
export function HoldDialog(props: Readonly<HoldDialogProps>): ReactNode {
	const { open, title, kind, initialReason, onCancel, onSubmit } = props;
	const max = new Date(props.max);
	const [day, setDay] = useState(utcDay(max));
	const [reason, setReason] = useState(initialReason ?? "");
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(false);
	const pickerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		setDay(utcDay(new Date(props.max)));
		setReason(initialReason ?? "");
		setError(undefined);
	}, [open, props.max, initialReason]);

	const limit =
		kind === "admin" ? BUNDLE_HOLD_LIMITS.adminMaxDays : BUNDLE_HOLD_LIMITS.selfMaxDays;

	const submit = async () => {
		// A cleared segment emits no change, so `day` still holds the last whole date: read the field itself.
		const incomplete = pickerRef.current?.querySelector(
			'[role="spinbutton"][data-placeholder]',
		);
		if (incomplete || !/^\d{4}-\d{2}-\d{2}$/.test(day)) {
			setError("Pick the day the hold ends.");
			return;
		}
		// Typed past the picker's max: refuse rather than shorten the hold behind the caller's back.
		if (day > utcDay(max)) {
			setError(`Pick a day no later than ${deleteOnText(max.toISOString())}.`);
			return;
		}
		const request = { holdUntil: holdUntilFor(day, max), reason: reason.trim() };
		const errors = bundleHoldRequestErrors(request, kind, new Date());
		if (errors.length > 0) {
			setError(errors.join("; "));
			return;
		}
		setBusy(true);
		const refused = await onSubmit(request);
		setBusy(false);
		if (refused) setError(refused);
	};

	return (
		<BuiDialog
			isOpen={open}
			onOpenChange={(isOpen) => {
				if (!isOpen && !busy) onCancel();
			}}
			width={460}
		>
			<DialogHeader>{title}</DialogHeader>
			<DialogBody>
				<Flex direction="column" gap="3">
					<DatePicker
						ref={pickerRef}
						label="Keep until"
						description={`At most ${limit} days from today. Holds end at midnight UTC.`}
						defaultValue={asDateValue<"defaultValue">(utcDay(max))}
						onChange={(value) => setDay(value ? String(value) : "")}
						minValue={asDateValue<"minValue">(utcDay(new Date()))}
						maxValue={asDateValue<"maxValue">(utcDay(max))}
						isRequired
					/>
					<TextAreaField
						label="Reason"
						description="For example: parental leave, or a client engagement on another laptop."
						value={reason}
						onChange={setReason}
						maxLength={BUNDLE_HOLD_LIMITS.reasonMaxLength}
						rows={3}
						isRequired
					/>
					{error ? (
						<Text as="p" color="danger" role="alert">
							{error}
						</Text>
					) : null}
				</Flex>
			</DialogBody>
			<DialogFooter>
				<BuiButton variant="secondary" onPress={onCancel} isDisabled={busy}>
					Cancel
				</BuiButton>
				<BuiButton variant="primary" onPress={submit} isPending={busy}>
					Save hold
				</BuiButton>
			</DialogFooter>
		</BuiDialog>
	);
}

/**
 * The owner's hold on their own bundle of a type. Renders nothing until the read succeeds, or when the
 * caller has no bundle of the type, so a failed read never blocks the page.
 */
export function MyBundleHold(
	props: Readonly<{
		bundleType: string;
		/** What the owner calls the bundle, e.g. "desktop". */
		noun: string;
		/** Told the active hold after every read and change, so the page can drop a reclaim date it covers. */
		onHoldChange?: (hold: BundleHold | undefined) => void;
	}>,
): ReactNode {
	const classes = useStyles();
	const { bundleType, noun, onHoldChange } = props;
	const request = useHoldRequest();
	const [state, setState] = useState<MyBundleHoldResponse>();
	const [dialog, setDialog] = useState(false);
	const [error, setError] = useState<string>();

	const settle = useCallback(
		(next: MyBundleHoldResponse) => {
			setState(next);
			onHoldChange?.(next.hold);
		},
		[onHoldChange],
	);

	useEffect(() => {
		let active = true;
		void request<MyBundleHoldResponse>("GET", bundleMyHoldPath(bundleType)).then((r) => {
			// A body without a valid limit is a failed read: render nothing rather than a broken dialog.
			if (active && r.value && !Number.isNaN(Date.parse(r.value.selfMaxUntil)))
				settle(r.value);
		});
		return () => {
			active = false;
		};
	}, [request, bundleType, settle]);

	if (!state?.slug) return null;
	const { hold, refused, selfHoldsLeft } = state;
	const refusedText = selfHoldRefusalText(refused);

	const place = async (body: BundleHoldRequest) => {
		const r = await request<MyBundleHoldResponse>("PUT", bundleMyHoldPath(bundleType), body);
		if (r.error !== undefined) return r.error;
		settle(r.value);
		setError(undefined);
		setDialog(false);
		return undefined;
	};
	// The limit counts from now, so read it again rather than open with one from an earlier read.
	const open = async () => {
		const r = await request<MyBundleHoldResponse>("GET", bundleMyHoldPath(bundleType));
		if (r.error !== undefined || Number.isNaN(Date.parse(r.value.selfMaxUntil))) {
			setError(r.error ?? "The hold could not be read. Try again.");
			return;
		}
		settle(r.value);
		setError(undefined);
		if (!r.value.refused) setDialog(true);
	};
	const clear = async () => {
		const r = await request<MyBundleHoldResponse>("DELETE", bundleMyHoldPath(bundleType));
		if (r.error !== undefined) setError(r.error);
		else {
			settle(r.value);
			setError(undefined);
		}
	};

	return (
		<>
			<div className={classes.row}>
				<Text as="p" variant="body-small" color="secondary">
					{hold
						? myHoldText(hold)
						: `Away for a while? A hold keeps this ${noun} for up to ${BUNDLE_HOLD_LIMITS.selfMaxDays} days even if you don't use it.`}
					{!refused && selfHoldsLeft <= BUNDLE_HOLD_LIMITS.selfMaxRenewals
						? ` ${selfHoldsLeft} of ${BUNDLE_HOLD_LIMITS.selfMaxRenewals + 1} self-service holds left.`
						: ""}
				</Text>
				{hold?.kind === "admin" ? null : (
					<div className={classes.actions}>
						<BuiButton
							size="small"
							variant="secondary"
							isDisabled={Boolean(refused)}
							onPress={open}
						>
							{hold ? "Extend hold" : `Keep this ${noun}`}
						</BuiButton>
						{hold ? (
							<BuiButton size="small" variant="tertiary" onPress={clear}>
								Clear hold
							</BuiButton>
						) : null}
					</div>
				)}
			</div>
			{refused === "renewals-used" || error ? (
				<Text as="p" variant="body-small" className={classes.danger} role="alert">
					{error ?? refusedText}
				</Text>
			) : null}
			<HoldDialog
				open={dialog}
				title={hold ? `Extend the hold on your ${noun}` : `Keep your ${noun}`}
				kind="self"
				max={state.selfMaxUntil}
				initialReason={hold?.reason}
				onCancel={() => setDialog(false)}
				onSubmit={place}
			/>
		</>
	);
}

/** The admin hold form for one bundle: opens while `max` is set, PUTs the hold, then closes and reports. */
export function AdminHoldDialog(
	props: Readonly<{
		bundleType: string;
		bundleId?: string;
		slug: string;
		/** The reason of the hold being changed, if any. */
		holdReason?: string;
		/** The admin limit counted from when the dialog opened; `undefined` keeps it closed. */
		max?: string;
		onClose: () => void;
		onChanged?: () => void;
	}>,
): ReactNode {
	const { bundleType, bundleId, slug, holdReason, max, onClose, onChanged } = props;
	const request = useHoldRequest();
	const place = async (body: BundleHoldRequest) => {
		const r = await request<BundleHoldResponse>(
			"PUT",
			bundleHoldPath(bundleType, slug, bundleId),
			body,
		);
		if (r.error !== undefined) return r.error;
		onClose();
		onChanged?.();
		return undefined;
	};
	return (
		<HoldDialog
			open={max !== undefined}
			title={`Hold ${slug}'s ${bundleType} bundle${bundleId ? ` ${bundleId}` : ""}`}
			kind="admin"
			max={max ?? new Date().toISOString()}
			initialReason={holdReason}
			onCancel={onClose}
			onSubmit={place}
		/>
	);
}

/** An admin's hold controls for any bundle: place, extend or shorten, and clear. */
export function AdminBundleHold(
	props: Readonly<{
		bundleType: string;
		/** One of a colleague's several bundles of the type; absent for the only one. */
		bundleId?: string;
		slug: string;
		holdUntil?: string;
		holdReason?: string;
		holdSetBy?: string;
		/** Called after a change lands, so the page can re-read the row. */
		onChanged?: () => void;
	}>,
): ReactNode {
	const { bundleType, bundleId, slug, holdUntil, holdReason, holdSetBy, onChanged } = props;
	const request = useHoldRequest();
	// The admin limit counts from when the dialog opens; set only while it is open.
	const [dialogMax, setDialogMax] = useState<string>();
	const [error, setError] = useState<string>();
	const held = isHoldActive(holdUntil, new Date());
	// Nothing hands focus back (the form has no DialogTrigger); without it Escape misses the sheet.
	const placeRef = useRef<HTMLButtonElement>(null);
	const closeForm = () => {
		setDialogMax(undefined);
		requestAnimationFrame(() => placeRef.current?.focus());
	};
	const clear = async () => {
		const r = await request<BundleHoldResponse>(
			"DELETE",
			bundleHoldPath(bundleType, slug, bundleId),
		);
		if (r.error !== undefined) setError(r.error);
		else {
			setError(undefined);
			onChanged?.();
		}
	};

	return (
		<>
			<Flex direction="column" gap="2">
				<Text as="p" variant="body-medium">
					{held
						? `Held until ${deleteOnText(holdUntil!)}${holdSetBy ? ` by ${holdSetBy}` : ""}${holdReason ? `: ${holdReason}` : ""}`
						: "No hold: inactivity reclaim applies."}
				</Text>
				<Flex gap="2">
					<BuiButton
						ref={placeRef}
						size="small"
						variant="secondary"
						onPress={() =>
							setDialogMax(holdMaxUntil("admin", new Date()).toISOString())
						}
					>
						{held ? "Change hold" : "Place hold"}
					</BuiButton>
					{held ? (
						<BuiButton size="small" variant="tertiary" onPress={clear}>
							Clear hold
						</BuiButton>
					) : null}
				</Flex>
				{error ? (
					<Text as="p" color="danger" role="alert">
						{error}
					</Text>
				) : null}
			</Flex>
			<AdminHoldDialog
				bundleType={bundleType}
				bundleId={bundleId}
				slug={slug}
				holdReason={held ? holdReason : undefined}
				max={dialogMax}
				onClose={closeForm}
				onChanged={() => {
					setError(undefined);
					onChanged?.();
				}}
			/>
		</>
	);
}
